import { Component, OnDestroy, computed, effect, inject, signal, untracked } from '@angular/core';
import { MenuController, NavController, Platform } from '@ionic/angular';
import { Router, NavigationEnd } from '@angular/router';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
import { filter, Subscription } from 'rxjs';

import { Constants } from '../../../providers/constants/constants';
import { AuthService } from '../../service/auth/auth.service';
import { SessionService } from '../../service/session/session.service';
import { ToolsService } from '../../service/tools/tools.service';

interface MenuPage {
  id: string;
  title: string;
  url: string;
  icon: string;
  data: {
    logout?: boolean;
    dataEvento?: {
      texto: string;
      url: string;
    };
  };
}

@Component({
  selector: 'app-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  standalone: false,
})
export class MenuComponent implements OnDestroy {
  private readonly menuCtrl = inject(MenuController);
  private readonly router = inject(Router);
  private readonly navController = inject(NavController);
  private readonly authService = inject(AuthService);
  private readonly toolsService = inject(ToolsService);
  private readonly platform = inject(Platform);
  private readonly sessionService = inject(SessionService);

  readonly constants = Constants;
  readonly versioApp = this.constants.versioApp;

  private readonly currentUrl = signal(this.normalizarUrl(this.router.url));
  private routerEventsSub?: Subscription;

  readonly nameUSer = computed(() => {
    const user = this.sessionService.user();
    if (!user) {
      return '';
    }
    return `${user['nombres'] ?? ''} ${user['apellidos'] ?? ''}`.trim();
  });

  readonly nameRoleUser = computed(() => {
    const roles = this.sessionService.user()?.['roles'] as { nombre?: string } | undefined;
    return roles?.nombre ?? '';
  });

  readonly appPages = computed(() => {
    if (this.esRutaPublica(this.normalizarUrl(this.currentUrl()))) {
      return [] as MenuPage[];
    }

    const user = this.sessionService.user();
    if (!user || !this.sessionService.isAuthenticated()) {
      return [] as MenuPage[];
    }

    return this.construirPaginasMenu(user);
  });

  /** Opciones adicionales del menú antes de personalizarlas por tipo de servicio. */
  private readonly additionalPagesBase: MenuPage[] = [
    {
      id: 'share-autoagendamiento',
      title: 'Autoagendamiento de citas',
      url: '/searchtypesofprocedures',
      icon: 'share-social-outline',
      data: {
        logout: false,
        dataEvento: {
          texto: 'Agenda tu cita aquí',
          url: 'https://agendateapp.com/citas/',
        },
      },
    },
    {
      id: 'share-lista-precios',
      title: 'Lista de precios',
      url: '/searchtypesofprocedures',
      icon: 'share-social-outline',
      data: {
        logout: false,
        dataEvento: {
          texto: 'Conoce nuestros servicios aquí',
          url: 'https://agendateapp.com/servicios/',
        },
      },
    },
    {
      id: 'configuraciones',
      title: 'Configuración',
      url: '/configuraciones',
      icon: 'settings-outline',
      data: {
        logout: false,
      },
    },
    {
      id: 'logout',
      title: 'Cerrar sesión',
      url: '/login',
      icon: 'log-in-outline',
      data: {
        logout: true,
      },
    },
  ];

  constructor() {
    this.suscribirCambiosRuta();

    effect(() => {
      const url = this.normalizarUrl(this.currentUrl());
      const isPublic = this.esRutaPublica(url);
      const hasSession = this.sessionService.isAuthenticated();

      untracked(() => {
        this.menuCtrl.enable(!isPublic && hasSession);
      });
    });
  }

  ngOnDestroy() {
    this.routerEventsSub?.unsubscribe();
  }

  /**
   * Escucha cambios de ruta para habilitar o deshabilitar el menú en vistas públicas.
   */
  private suscribirCambiosRuta(): void {
    this.routerEventsSub = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.currentUrl.set(this.normalizarUrl(event.urlAfterRedirects || event.url));
      });
  }

  /**
   * Normaliza la URL eliminando query params y fragmentos.
   */
  private normalizarUrl(url: string): string {
    const path = (url || '/').split('?')[0].split('#')[0];
    return path || '/';
  }

  /**
   * Determina si la ruta actual corresponde a una vista pública sin menú lateral.
   */
  private esRutaPublica(url: string): boolean {
    if (this.toolsService.validateUrlInactivateMenu(url)) {
      return true;
    }

    return url.startsWith('/registeraccount') || url.startsWith('/sliderhome');
  }

  /**
   * Construye las opciones del menú lateral según el usuario en sesión.
   */
  private construirPaginasMenu(user: Record<string, unknown>): MenuPage[] {
    const organizaciones = user['organizaciones'] as { _id?: string; tipo_servicio?: string } | undefined;
    const idOrganizacionAgendate = organizaciones?._id ?? '';
    const tipoServicioEsTalle = organizaciones?.tipo_servicio === 'serv_taller';
    const tipoServicioEsTransporte = organizaciones?.tipo_servicio === 'serv_transporte';
    const lenguajeApp = tipoServicioEsTalle
      ? this.constants.lenguajeApp.menu.tipoTaller
      : this.constants.lenguajeApp.menu.tipoCita;

    let additionalPages = structuredClone(this.additionalPagesBase);

    if (tipoServicioEsTalle) {
      additionalPages.splice(-1, 0, {
        id: 'listvehiculos',
        title: 'Vehículos',
        url: '/listvehiculos',
        icon: 'car-sport-outline',
        data: {
          logout: false,
        },
      });
    } else if (tipoServicioEsTransporte || idOrganizacionAgendate === '5eadcc4f3b6a8b061510f9d3') {
      additionalPages.splice(-1, 0, {
        id: 'admin-listvehiculos',
        title: 'Administrar vehiculos',
        url: '/admin-listvehiculos',
        icon: 'car-sport-outline',
        data: {
          logout: false,
        },
      });
    }

    // Validación por ahora para no mostrar enlace de detalle de plan en iOS.
    if (Capacitor.getPlatform() === 'ios') {
      additionalPages = [{
        id: 'logout-ios',
        title: 'Cerrar sesión',
        url: '/login',
        icon: 'log-in-outline',
        data: {
          logout: true,
        },
      }];
    }

    return [
      {
        id: 'home',
        title: lenguajeApp.dashboard,
        url: '/home',
        icon: 'calendar-outline',
        data: {
          logout: false,
        },
      },
      {
        id: 'adminusers',
        title: 'Administrar usuarios',
        url: '/adminusers',
        icon: 'people-outline',
        data: {
          logout: false,
        },
      },
      {
        id: 'estadisticas',
        title: 'Estadisticas',
        url: '/estadisticas',
        icon: 'stats-chart-outline',
        data: {
          logout: false,
        },
      },
      ...additionalPages,
    ];
  }

  /** sub_dominio en sesión (snake o camel) desde objeto usuario */
  private subDominioFromUser(user: Record<string, unknown> | null): string {
    const org = user?.['organizaciones'] as { sub_dominio?: string; subDominio?: string } | undefined;
    if (!org) {
      return '';
    }
    const value = org.sub_dominio ?? org.subDominio;
    return value != null && value !== '' ? String(value) : '';
  }

  openPage(page: MenuPage) {
    if (page.data.logout) {
      this.logout();
    } else {
      this.validateTokenRenewal();
      if (page.data?.dataEvento) {
        this.compartirEnlace(page.data.dataEvento);
        return;
      }
      this.router.navigate([page.url]);
    }
  }

  irAlPerfil() {
    this.menuCtrl.close();
    this.router.navigate(['organizacion']);
  }

  /**
   * Comparte url con la funcion de compartir del sispositivo movil, si esta en web valida que sea compatible si no
   * copia la url en el portapeles del usuario.
   */
  async compartirEnlace(data: { texto: string; url: string }) {
    const user = this.sessionService.user();
    const subDominio = this.subDominioFromUser(user);
    const url = `${data.url}${subDominio}`;
    const dataShare = {
      title: '',
      text: data.texto,
      url,
      dialogTitle: 'Agéndate App',
    };

    if (this.platform.is('android') || this.platform.is('ios')) {
      await Share.share(dataShare);
    } else if (navigator.share) {
      navigator.share(dataShare)
        .then(() => console.log(''))
        .catch((error) => console.log('Error sharing', error));
    } else {
      navigator.clipboard.writeText(url)
        .then(() => console.log(''))
        .catch((error) => console.log('Error clipboard writeText', error));
      this.toolsService.presentToast('Enlace copiado.');
    }
  }

  validateTokenRenewal() {
    this.authService.renewToken()
      .subscribe({
        next: (response) => {
          console.log(response);
          console.log(response['token']);
          if (response['renew']) {
            this.toolsService.updateUserToken(response['token']);
          }
        },
        error: (err) => {
          this.toolsService.statusService(err);
        },
      });
  }

  logout() {
    this.toolsService.flowTrace('auth.logout.request', {
      userIdSuffix: (() => {
        const user = this.sessionService.user();
        return String(user?.['_id'] ?? '').slice(-6);
      })(),
    });
    const data = { token: localStorage.getItem(this.constants.localStorage.token) };
    this.authService.logout(data)
      .subscribe({
        next: () => {
          this.toolsService.flowTrace('auth.logout.response', { next: true });
          setTimeout(() => {
            this.toolsService.clearDataRedirectLogin();
          }, 500);
        },
        error: (err) => {
          this.toolsService.flowTrace('auth.logout.httpError', { status: err?.status });
          this.toolsService.statusService(err);
        },
      });
  }
}
